import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { Input } from "@hortis/ui/input";
import { type ReactNode, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { z } from "zod";

const schema = z.object({
  tagNumber: z.string().min(1).nullish(),
});

export const TagNumberDialog = ({
  open,
  onOpenChange,
  onSubmit,
  children,
  initialValue,
}: {
  initialValue: string | undefined | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (value: string | undefined | null) => void;
  children: ReactNode;
}) => {
  const form = useForm<z.infer<typeof schema>>({
    defaultValues: { tagNumber: initialValue ?? undefined },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    form.reset({ tagNumber: initialValue ?? undefined });
  }, [open, initialValue, form]);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    onSubmit(data.tagNumber);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
        {children}
      </DialogTrigger>
      <FormProvider {...form}>
        <DialogContent className="gap-5!" asChild>
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              void form.handleSubmit(handleSubmit)(e);
            }}
          >
            <DialogHeader className="space-y-4">
              <DialogTitle className="flex items-center gap-2">
                Set tag number
              </DialogTitle>
              <FormField
                control={form.control}
                name="tagNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        value={field.value ?? undefined}
                        onChange={(e) => {
                          e.target.value === ""
                            ? field.onChange(null)
                            : field.onChange(e.target.value);
                        }}
                        placeholder="Enter tag number..."
                        data-cy="tag-number-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button>Cancel</Button>
              </DialogClose>
              <Button variant="primary" type="submit" testId="save-tag-number">
                Set tag number
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
